import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { Dialog as RDialog, DropdownMenu as RMenu, Tooltip as RTooltip } from 'radix-ui';
import { Drawer } from 'vaul';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { X } from 'lucide-react';
import { cn } from '../lib/cn';
import { useIsTabletUp } from '../lib/hooks';
import { durations, easings, springs } from '../lib/motion';
import { Button } from './button';

type SheetSize = 'sm' | 'md' | 'lg' | 'xl';
const DIALOG_WIDTH: Record<SheetSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Ações fixas no rodapé (ex.: botão principal). */
  footer?: ReactNode;
  size?: SheetSize;
  /** Esconde o título visualmente (continua acessível). */
  hideTitle?: boolean;
  className?: string;
  /** Impede fechar clicando fora (formulários longos). */
  modalLocked?: boolean;
}

function CloseButton({ asDrawer }: { asDrawer?: boolean }) {
  const Close = asDrawer ? Drawer.Close : RDialog.Close;
  return (
    <Close
      className="-mr-1.5 -mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full text-ink-3 transition-[background-color,color,scale] duration-150 hover:bg-ink/[0.06] hover:text-ink active:scale-90"
      aria-label="Fechar"
    >
      <X className="size-5" />
    </Close>
  );
}

/** Diálogo centralizado (desktop), com entrada em mola. */
export function Dialog({ open, onOpenChange, title, description, children, footer, size = 'md', hideTitle, className, modalLocked }: SheetProps) {
  const reduce = useReducedMotion();
  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open ? (
          <RDialog.Portal forceMount>
            <RDialog.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-scrim backdrop-blur-[3px]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: durations.base, ease: easings.standard }}
              />
            </RDialog.Overlay>
            <RDialog.Content
              asChild
              forceMount
              onPointerDownOutside={modalLocked ? (e) => e.preventDefault() : undefined}
            >
              <motion.div
                className={cn(
                  'fixed left-1/2 top-1/2 z-50 flex max-h-[88vh] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-sheet bg-surface shadow-pop outline-none',
                  DIALOG_WIDTH[size],
                  className,
                )}
                initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.98, y: 4, transition: { duration: durations.fast, ease: easings.standard } }}
                transition={reduce ? { duration: durations.fast } : springs.gentle}
              >
                <div className={cn('flex items-start gap-3 px-5 pt-5', hideTitle && 'sr-only')}>
                  <div className="min-w-0 flex-1">
                    <RDialog.Title className="text-lg font-semibold tracking-[-0.015em] text-ink">{title}</RDialog.Title>
                    {description ? <RDialog.Description className="mt-1 text-sm text-ink-2">{description}</RDialog.Description> : null}
                  </div>
                  <CloseButton />
                </div>
                {!description ? <RDialog.Description className="sr-only">{typeof title === 'string' ? title : 'Diálogo'}</RDialog.Description> : null}
                <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
                {footer ? (
                  <div className="flex flex-col-reverse gap-2 rounded-b-sheet border-t border-line bg-surface-2/50 px-5 py-4 sm:flex-row sm:justify-end">{footer}</div>
                ) : null}
              </motion.div>
            </RDialog.Content>
          </RDialog.Portal>
        ) : null}
      </AnimatePresence>
    </RDialog.Root>
  );
}

/** Gaveta inferior (celular), com alça, cantos de 24 px e arraste para fechar. */
export function BottomSheet({ open, onOpenChange, title, description, children, footer, hideTitle, className, modalLocked }: SheetProps) {
  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange} dismissible={!modalLocked} repositionInputs={false}>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-50 bg-scrim" />
        <Drawer.Content
          className={cn(
            'fixed inset-x-0 bottom-0 z-50 flex max-h-[92dvh] flex-col rounded-t-sheet bg-surface shadow-pop outline-none',
            className,
          )}
        >
          <div className="mx-auto mt-2.5 h-1.5 w-10 shrink-0 rounded-full bg-ink/15" aria-hidden="true" />
          <div className={cn('flex items-start gap-3 px-5 pb-1 pt-3', hideTitle && 'sr-only')}>
            <div className="min-w-0 flex-1">
              <Drawer.Title className="text-lg font-semibold tracking-[-0.015em] text-ink">{title}</Drawer.Title>
              {description ? <Drawer.Description className="mt-1 text-sm text-ink-2">{description}</Drawer.Description> : null}
            </div>
            <CloseButton asDrawer />
          </div>
          {!description ? <Drawer.Description className="sr-only">{typeof title === 'string' ? title : 'Painel'}</Drawer.Description> : null}
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-3">{children}</div>
          {footer ? (
            <div className="flex flex-col gap-2 border-t border-line px-5 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3">{footer}</div>
          ) : (
            <div className="pb-[calc(0.75rem+env(safe-area-inset-bottom))]" />
          )}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

/** Bottom sheet no celular e diálogo no tablet/desktop: mesma API. */
export function Sheet(props: SheetProps) {
  const wide = useIsTabletUp();
  return wide ? <Dialog {...props} /> : <BottomSheet {...props} />;
}

// ---------------------------------------------------------------- Confirmação
interface ConfirmOptions {
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'primary' | 'danger';
}

const ConfirmContext = createContext<((opts: ConfirmOptions) => Promise<boolean>) | null>(null);

/** Provedor do diálogo de confirmação. Use `const confirm = useConfirm()`. */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<(ConfirmOptions & { open: boolean }) | null>(null);
  const resolver = useRef<((v: boolean) => void) | null>(null);
  const confirm = useCallback((opts: ConfirmOptions) => {
    setState({ ...opts, open: true });
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);
  const close = (value: boolean) => {
    resolver.current?.(value);
    resolver.current = null;
    setState((s) => (s ? { ...s, open: false } : s));
  };
  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state ? (
        <Sheet
          open={state.open}
          onOpenChange={(o) => !o && close(false)}
          title={state.title}
          description={state.description}
          size="sm"
          footer={
            <>
              <Button variant="outline" onClick={() => close(false)} block>
                {state.cancelLabel ?? 'Cancelar'}
              </Button>
              <Button variant={state.tone === 'danger' ? 'danger' : 'primary'} onClick={() => close(true)} block autoFocus>
                {state.confirmLabel ?? 'Confirmar'}
              </Button>
            </>
          }
        />
      ) : null}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm precisa do <ConfirmProvider>');
  return ctx;
}

// ---------------------------------------------------------------- Menu contextual (...)
export interface MenuItem {
  label: ReactNode;
  icon?: ReactNode;
  onSelect: () => void;
  tone?: 'default' | 'danger';
  disabled?: boolean;
  hidden?: boolean;
  /** Atalho exibido à direita (ex.: "Ctrl K"). */
  shortcut?: string;
}

export function Menu({
  trigger,
  items,
  align = 'end',
  label,
}: {
  trigger: ReactNode;
  items: (MenuItem | 'separator')[];
  align?: 'start' | 'end';
  label?: string;
}) {
  const visible = items.filter((i) => i === 'separator' || !i.hidden);
  const [open, setOpen] = useState(false);
  const touch = useRef(false);
  const closedAt = useRef(0);
  const onOpenChange = (next: boolean) => {
    if (!next) closedAt.current = Date.now();
    setOpen(next);
  };
  return (
    <RMenu.Root open={open} onOpenChange={onOpenChange}>
      <RMenu.Trigger
        asChild
        aria-label={label}
        // O Radix abre no pointerdown, inclusive no toque: rolar a lista com o dedo sobre o "..."
        // abriria o menu sem querer. No toque, abre só no clique (toque completo, sem arrastar).
        onPointerDown={(e) => {
          touch.current = e.pointerType !== 'mouse';
          if (touch.current) e.preventDefault();
        }}
        onClick={() => {
          // Toque no gatilho com o menu aberto: o toque "fora" já fechou. Não reabre em seguida.
          if (touch.current && !open && Date.now() - closedAt.current > 300) setOpen(true);
        }}
      >
        {trigger}
      </RMenu.Trigger>
      <RMenu.Portal>
        <RMenu.Content
          align={align}
          sideOffset={6}
          collisionPadding={8}
          className="z-50 min-w-52 origin-[var(--radix-dropdown-menu-content-transform-origin)] rounded-xl bg-elevated p-1.5 shadow-pop data-[state=open]:animate-scale-in"
        >
          {visible.map((item, i) =>
            item === 'separator' ? (
              <RMenu.Separator key={`s${i}`} className="mx-1.5 my-1 h-px bg-line" />
            ) : (
              <RMenu.Item
                key={i}
                disabled={item.disabled}
                onSelect={item.onSelect}
                className={cn(
                  'flex cursor-pointer select-none items-center gap-2.5 rounded-lg px-3 py-2.5 text-[15px] outline-none transition-colors duration-100',
                  'data-[disabled]:pointer-events-none data-[disabled]:opacity-40 [&_svg]:size-[18px]',
                  item.tone === 'danger' ? 'text-danger data-[highlighted]:bg-danger-soft' : 'text-ink data-[highlighted]:bg-ink/[0.06]',
                )}
              >
                {item.icon ? <span className={item.tone === 'danger' ? 'text-danger' : 'text-ink-3'}>{item.icon}</span> : null}
                <span className="flex-1">{item.label}</span>
                {item.shortcut ? <span className="font-mono text-[11px] text-ink-3">{item.shortcut}</span> : null}
              </RMenu.Item>
            ),
          )}
        </RMenu.Content>
      </RMenu.Portal>
    </RMenu.Root>
  );
}

// ---------------------------------------------------------------- Tooltip
export function Tooltip({
  content,
  children,
  side = 'top',
}: {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
}) {
  return (
    <RTooltip.Provider delayDuration={300}>
      <RTooltip.Root>
        <RTooltip.Trigger asChild>{children}</RTooltip.Trigger>
        <RTooltip.Portal>
          <RTooltip.Content
            side={side}
            sideOffset={6}
            collisionPadding={8}
            className="z-50 max-w-64 origin-[var(--radix-tooltip-content-transform-origin)] animate-scale-in rounded-lg bg-dark px-2.5 py-1.5 text-xs font-medium text-on-dark shadow-pop ring-1 ring-white/10"
          >
            {content}
            <RTooltip.Arrow className="fill-dark" />
          </RTooltip.Content>
        </RTooltip.Portal>
      </RTooltip.Root>
    </RTooltip.Provider>
  );
}
